import BaseComponent from './BaseComponent';
import { getElementFromEvent } from '../utils';
import gsap from 'gsap';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import { getKeyboardFocusableElements, trapFocus, track } from '../utils';

class Menu extends BaseComponent {
	constructor(element) {
		super();

		this.element = element;
		this.breakpoint = window.matchMedia('(min-width: 1300px)');
		this.parser = new DOMParser();

		// State
		this.isOpen = false;
		this.isLanguageSwitcherOpen = false;
		this.firstLoad = true;
		this.trapFocus = null;

		// Events

		// Clicking outside language switcher or toggle should close the language switcher
		document.addEventListener('click', (e) => {
			if (!this.breakpoint.matches) {
				return;
			}

			if (!e.target) {
				return;
			}

			if (e.target.closest('[data-language-switcher-toggle]') || e.target.closest('[data-language-switcher]')) {
				return;
			}

			this.hideLanguageSwitcher();
		});

		this.element.addEventListener('click', (e) => {
			const button = getElementFromEvent(e, '[data-language-switcher-toggle]');

			if (!button) {
				return;
			}

			this.isLanguageSwitcherOpen ? this.hideLanguageSwitcher() : this.showLanguageSwitcher();
		});

		this.element.addEventListener('click', (e) => {
			const button = getElementFromEvent(e, '[data-menu-toggle]');

			if (!button) {
				return;
			}

			this.isOpen ? this.close() : this.open();
		});

		this.element.addEventListener('click', (e) => {
			const button = getElementFromEvent(e, '[data-language-switcher-close]');

			if (!button) {
				return;
			}

			this.hideLanguageSwitcher(true);
		});

		if (screen.orientation) {
			screen.orientation.addEventListener('change', (event) => {
				this.close();
			});
		} else {
			window.matchMedia('(orientation: portrait)').addEventListener('change', () => {
				this.close();
			});
		}

		document.addEventListener('leave', (e) => {
			if (this.breakpoint.matches) {
				this.hideLanguageSwitcher();
			} else {
				this.close();
			}
		});

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				this.close();
			}
		});

		document.addEventListener('bless', (e) => {
			if (this.firstLoad) {
				this.firstLoad = false;
				this.init();
				return;
			}

			this.replaceContent(e.detail?.next?.html);
			this.init();
		});
	}

	init() {
		this.languageSwitcher = this.element.querySelector('[data-language-switcher]');
		this.menuToggle = this.element.querySelector('[data-menu-toggle]');
		this.languageSwitcherToggle = this.element.querySelector('[data-language-switcher-toggle]');
		this.bg = this.element.querySelector('[data-menu-bg]');
		this.glass = this.element.querySelector('[data-menu-glass]');
		this.menuContent = this.element.querySelector('[data-menu-content]');
	}

	replaceContent(html) {
		if (!html) {
			return;
		}

		const parsed = this.parser.parseFromString(html, 'text/html');
		const newMenu = parsed.querySelector('[data-menu] [data-replace-content]');

		if (!newMenu) {
			return;
		}

		this.menuContent.replaceWith(newMenu);
	}

	open() {
		if (this.isOpen) {
			return;
		}

		const menuItems = this.element.querySelectorAll('.c-menu__menu .menu-item');
		const menuButtons = this.element.querySelectorAll('.c-menu__buttons > *');
		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		this.isOpen = true;
		this.element.classList.add('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'true');

		this.trapMainMenu();
		disablePageScroll();

		gsap.set([menuItems, menuButtons], {
			y: 10,
			autoAlpha: 0,
		});

		tl.to(this.glass, {
			autoAlpha: 1,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			this.bg,
			{
				autoAlpha: 1,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
			},
			'<'
		);

		tl.to(menuItems, {
			y: 0,
			autoAlpha: 1,
			stagger: prefersReducedMotion ? 0 : 0.1,
			duration: prefersReducedMotion ? 0 : 0.8,
			ease: 'power4.out',
		});

		tl.to(
			menuButtons,
			{
				y: 0,
				autoAlpha: 1,
				stagger: prefersReducedMotion ? 0 : 0.1,
				duration: prefersReducedMotion ? 0 : 0.8,
				ease: 'power4.out',
			},
			'<+=0.2'
		);

		return tl;
	}

	close() {
		if (!this.isOpen) {
			return;
		}

		this.isOpen = false;
		this.element.classList.remove('is-open');
		this.menuToggle.setAttribute('aria-expanded', 'false');

		this.trapFocus?.remove();
		this.trapFocus = null;

		enablePageScroll();

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		tl.to(this.bg, {
			autoAlpha: 0,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			this.glass,
			{
				autoAlpha: 0,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
			},
			'<'
		);

		tl.add(this.hideLanguageSwitcher());

		return tl;
	}

	showLanguageSwitcher() {
		if (this.isLanguageSwitcherOpen) {
			return;
		}

		this.isLanguageSwitcherOpen = true;
		this.languageSwitcherToggle.setAttribute('aria-expanded', 'true');

		// Mobile
		if (!this.breakpoint.matches) {
			this.trapFocus?.remove();
			this.trapLanguageSwitcher();
		}

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		tl.fromTo(
			this.languageSwitcher,
			{
				autoAlpha: 0,
			},
			{
				autoAlpha: 1,
				duration: prefersReducedMotion ? 0 : 0.3,
				ease: 'none',
				onComplete: () => {
					if (!this.breakpoint.matches) {
						this.trapFocus?.focus();
					}
				},
			}
		);

		return tl;
	}

	hideLanguageSwitcher(trapMainMenu = false) {
		if (!this.isLanguageSwitcherOpen) {
			return;
		}

		this.isLanguageSwitcherOpen = false;
		this.languageSwitcherToggle.setAttribute('aria-expanded', 'false');

		// Mobile
		if (!this.breakpoint.matches) {
			this.trapFocus?.remove();

			if (trapMainMenu) {
				this.trapMainMenu();
			}
		}

		const tl = gsap.timeline();
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		tl.to(this.languageSwitcher, {
			autoAlpha: 0,
			duration: prefersReducedMotion ? 0 : 0.3,
			ease: 'none',
		});

		return tl;
	}

	trapMainMenu() {
		this.trapFocus = trapFocus(this.element, [
			this.menuToggle,
			this.element.querySelector('.c-menu__logo-mobile'),
			...getKeyboardFocusableElements(this.element.querySelector('.c-menu__menu')),
			...getKeyboardFocusableElements(this.element.querySelector('.c-menu__buttons')),
		]);
	}

	trapLanguageSwitcher() {
		this.trapFocus = trapFocus(this.element, [
			...getKeyboardFocusableElements(this.languageSwitcher),
			this.menuToggle,
		]);
	}
}

export const menu = new Menu(document.querySelector('[data-menu]'));
